export const HEX_RE = /^#([0-9a-fA-F]{6})$/;

export function isHex(v) {
  return typeof v === "string" && HEX_RE.test(v.trim());
}

export function normHex(v) {
  return isHex(v) ? v.trim().toUpperCase() : v;
}

function lum(hex) {
  const m = HEX_RE.exec(hex || "");
  if (!m) return 0;
  const h = m[1];
  const ch = (c) => {
    c /= 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * ch(parseInt(h.slice(0, 2), 16)) + 0.7152 * ch(parseInt(h.slice(2, 4), 16)) + 0.0722 * ch(parseInt(h.slice(4, 6), 16));
}

export function contrast(a, b) {
  const l1 = lum(a), l2 = lum(b);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

export const MIN_CONTRAST = 3;

// Mirrors backend pick_text_color(): supporting copy leans on the SECONDARY first (keeps hierarchy
// under the display text); display text goes to the neutral. `current` is kept whenever it already reads.
const TEXT_CANDIDATES = {
  support: ["secondary", "accent", "neutral", "#FFFFFF", "darkest", "#000000"],
  hero: ["neutral", "#FFFFFF", "secondary", "darkest", "#000000"],
};

export function pickText(bg, kind, brand, current) {
  if (!isHex(bg)) return { color: current, role: "current" };
  if (isHex(current) && contrast(current, bg) >= MIN_CONTRAST) return { color: normHex(current), role: "current" };
  const palette = ["primary", "accent", "secondary", "neutral"].map((r) => brand[r]).filter(isHex);
  const darkest = palette.length ? palette.reduce((a, b) => (lum(a) <= lum(b) ? a : b)) : "#000000";
  const resolved = [];
  (TEXT_CANDIDATES[kind] || TEXT_CANDIDATES.support).forEach((c) => {
    const val = c === "darkest" ? darkest : c.startsWith("#") ? c : brand[c];
    if (isHex(val) && !resolved.some((r) => r.color === normHex(val))) resolved.push({ color: normHex(val), role: c });
  });
  return resolved.find((r) => contrast(r.color, bg) >= MIN_CONTRAST)
    || resolved.reduce((a, b) => (contrast(a.color, bg) >= contrast(b.color, bg) ? a : b));
}

export function contrastReport(brand) {
  const pairs = [
    ["Display text on primary card", "neutral", "primary", "hero"],
    ["Supporting copy on primary card", "accent", "primary", "support"],
    ["Display text on accent card", "primary", "accent", "hero"],
    ["Supporting copy on accent card", "primary", "accent", "support"],
  ];
  return pairs
    .filter(([, f, b]) => isHex(brand[f]) && isHex(brand[b]))
    .map(([label, f, b, kind]) => {
      const ratio = contrast(brand[f], brand[b]);
      const picked = pickText(brand[b], kind, brand, brand[f]);
      return {
        label, fg: brand[f], bg: brand[b], ratio: Math.round(ratio * 100) / 100, ok: ratio >= MIN_CONTRAST,
        picked: picked.color, pickedRole: picked.role, pickedRatio: Math.round(contrast(picked.color, brand[b]) * 100) / 100,
      };
    });
}

export function isLight(hex) {
  return lum(hex) > 0.6;
}

const loaded = new Set();
export function loadGoogleFont(family) {
  if (!family || loaded.has(family)) return;
  loaded.add(family);
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, "+")}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

export const ROLE_LABELS = { primary: "Primary", accent: "Accent", secondary: "Secondary", neutral: "Neutral" };
export const ROLE_HELP = {
  primary: "Full-screen brand cards & detail panel",
  accent: "CTA card, eyebrows & highlight copy",
  secondary: "Supporting copy on cards when the accent can't be read",
  neutral: "Display text on dark cards — usually white",
};
